import { addMinutes, getTime } from "date-fns";
import React, { useCallback } from "react";
import { AccountContact } from "../../../api/services";
import {
  Box,
  IconButton,
  Grid,
  Paper,
  Typography,
  Collapse,
  styled,
  Button,
} from "@material-ui/core";
import { useSessionStorage } from "../../../hooks";
import {
  ChevronDownIcon as BaseChevronDown,
  ChevronUpIcon as BaseChevronUp,
  CustomerIcon as BaseCustomerIcon,
} from "../../../icons";
import { Field } from "./Field";
import { formatAddress } from "../../../utils/formatAddress";
import { Link, useRouteMatch } from "react-router-dom";
import { formatContactName } from "../../../utils/formatName";

const ChevronDownIcon = styled(BaseChevronDown)(({ theme }) => ({
  fill: theme.palette.grey["900"],
}));
const ChevronUpIcon = styled(BaseChevronUp)(({ theme }) => ({
  fill: theme.palette.grey["900"],
}));
const CustomerIcon = styled(BaseCustomerIcon)(({ theme }) => ({
  fill: theme.palette.grey["900"],
}));

interface ContactProps {
  contact: AccountContact;
}
// Only first word is capitalized
const formatTitleCase = (title: string) => {
  const lower = title.toLowerCase().trim();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const Contact: React.FC<ContactProps> = ({ contact }) => {
  const [isExpanded, setIsExpanded] = useSessionStorage(
    `Contact(${contact.id}).isExpanded`,
    false,
  );

  const handleToggle = useCallback(() => {
    const ttl = getTime(addMinutes(Date.now(), 30));
    setIsExpanded((state) => !state, ttl);
  }, [setIsExpanded]);

  const { url } = useRouteMatch();

  return (
    <Box component={Paper} mb={3} data-testid="Contact" display="flex" py={2}>
      <Box display="inline-flex" mx={2} data-testid="Contact__Icon" py={0.5}>
        <CustomerIcon />
      </Box>
      <Box width={1}>
        <Grid container>
          <Grid
            item
            xs={12}
            md={isExpanded ? 12 : 4}
            lg={4}
            data-testid="Contact__Detail--Main"
          >
            <Box px={1} mb={[2, isExpanded ? 2 : 0, 0]}>
              <Typography variant="subtitle1" color="textPrimary">
                {formatContactName(contact)}
              </Typography>
              {contact.isPrimary && (
                <Typography variant="body2" color="textSecondary">
                  Primary contact
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid
            item
            xs
            md={isExpanded ? 6 : 4}
            lg={4}
            data-testid="Contact__Detail--BillDelivery"
          >
            <Collapse in={isExpanded}>
              <Box mt={[2, 2, 0]} />
            </Collapse>
            <Field
              title="Bill delivery"
              subtitle={
                contact.billDelivery.length > 0
                  ? contact.billDelivery.join(", ")
                  : "-"
              }
              gutterBottom={false}
            />
          </Grid>
          <Grid
            item
            xs
            md={isExpanded ? 6 : 4}
            lg={4}
            data-testid="Contact__Detail--CommDelivery"
          >
            <Collapse in={isExpanded}>
              <Box mt={[2, 2, 0]} />
            </Collapse>
            <Field
              title="Communication delivery"
              subtitle={
                contact.commDelivery.length > 0
                  ? contact.commDelivery.join(", ")
                  : "-"
              }
              gutterBottom={false}
            />
          </Grid>
        </Grid>
        <Collapse in={isExpanded}>
          <Collapse in={isExpanded}>
            <Box mt={2} />
          </Collapse>
          <Box>
            <Grid
              container
              alignItems="flex-start"
              data-testid="Contact__Expanded"
            >
              <Grid item xs md={6} lg={4}>
                {contact.type && (
                  <Field title="Contact type" subtitle={contact.type.name} />
                )}
                {contact.dateOfBirthDt && (
                  <Field
                    title="Date of birth"
                    subtitle={contact.dateOfBirthDt}
                  />
                )}
                {contact.securityNumber && (
                  <Field
                    title="Security Number"
                    subtitle={contact.securityNumber}
                  />
                )}
              </Grid>
              <Grid item xs md={6} lg={4}>
                {contact.email && (
                  <Field title="Email" subtitle={contact.email} />
                )}
                {contact.number1 && (
                  <Field
                    title={
                      contact.type.number1Label
                        ? formatTitleCase(contact.type.number1Label)
                        : "Number 1"
                    }
                    subtitle={contact.number1}
                  />
                )}
                {contact.number2 && (
                  <Field
                    title={
                      contact.type.number2Label
                        ? formatTitleCase(contact.type.number2Label)
                        : "Number 2"
                    }
                    subtitle={contact.number2}
                  />
                )}
                {contact.number3 && (
                  <Field
                    title={
                      contact.type.number3Label
                        ? formatTitleCase(contact.type.number3Label)
                        : "Number 3"
                    }
                    subtitle={contact.number3}
                  />
                )}
              </Grid>
              <Grid item xs md={12} lg={4}>
                {contact.address && (
                  <Field
                    title="Billing address"
                    subtitle={formatAddress(contact.address)}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
          <Box mt={0.5} mb={1} px={1}>
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to={`${url}/contacts/${contact.id}/`}
            >
              Edit
            </Button>
          </Box>
        </Collapse>
      </Box>
      <Box mr={0.5}>
        <IconButton data-testid="Contact__Button" onClick={handleToggle}>
          {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </IconButton>
      </Box>
    </Box>
  );
};
